// src/utils/heroMatchups.js
// "ฮีโร่ตัวนี้ / คอมนี้ เจอตัวไหนบ่อย แล้ววินเรทเท่าไหร่"
//
// อ่านจาก log เกมต่อเกม (games[] จาก parseGamesFromExcel) เท่านั้น เพราะชีทสรุป
// (RPL_player_stats / ComboStats / Competitor_Hero_stats) เก็บแต่ยอดรวม ไม่มีฮีโร่
// ของฝ่ายตรงข้ามรายเกม — จำนวนเกมที่ได้จากไฟล์นี้จึงอาจไม่เท่าตัวเลขบนการ์ด
import { canonicalHeroName } from "./heroCatalog";

export const MATCHUP_ROLES = ["DSL", "JUG", "MID", "ADL", "SUP"];

export function normalizeRole(pos) {
  if (!pos) return "";
  const s = String(pos).toLowerCase().replace(/\s+/g, "").replace(/^(fs|op|opp|enemy)/, "");
  if (["dsl", "ds", "solo", "slayer"].includes(s)) return "DSL";
  if (["mid", "midlane"].includes(s)) return "MID";
  if (["adl", "adc", "dl"].includes(s)) return "ADL";
  if (["jungle", "jug", "jgl", "jg"].includes(s)) return "JUG";
  if (["roaming", "roam", "sup", "support"].includes(s)) return "SUP";
  return String(pos).trim().toUpperCase();
}

/** "2-heroes (ADL+SUP)" → ["ADL","SUP"] — การ์ดคอมโบผูกกับตำแหน่ง ไม่ใช่แค่ "อยู่ทีมเดียวกัน"
    ชื่อคอมโบเรียงตามลำดับตำแหน่งในป้ายเสมอ (ทั้งที่สร้างจาก log และจากชีท) */
export function rolesFromComboLabel(label) {
  const inside = String(label || "").match(/\(([^)]+)\)/);
  if (!inside) return [];
  const roles = inside[1].split("+").map((t) => normalizeRole(t));
  return roles.every((r) => MATCHUP_ROLES.includes(r)) ? roles : [];
}

export function isFsWin(game) {
  return (
    game?.win === 1 ||
    game?.win === "1" ||
    String(game?.result || "").toLowerCase() === "win"
  );
}

// ช่องสูตรที่พังในชีท (#N/A, #DIV/0! ฯลฯ) หลุดผ่าน parser มาได้ — กันไม่ให้ขึ้นเป็นแถวฮีโร่
const NOT_A_HERO = /^(#[a-z0-9/]+!?|-+|nan)$/i;

function heroNameOf(value) {
  const raw = String(value ?? "").trim();
  if (!raw || NOT_A_HERO.test(raw)) return "";
  return canonicalHeroName(raw);
}

/** role → ฮีโร่ ของเกมหนึ่ง: ใช้คอลัมน์ per-role ที่ parser ทำไว้ก่อน แล้วเติมจาก pick+position */
export function rolePicks(game, side = "fs") {
  const preset = side === "op" ? game?.opByRole : game?.fsByRole;
  const out = { DSL: "", JUG: "", MID: "", ADL: "", SUP: "" };

  MATCHUP_ROLES.forEach((role) => {
    out[role] = heroNameOf(preset?.[role]);
  });

  const picks = side === "op" ? game?.opPick : game?.fsPick;
  const positions = side === "op" ? game?.opPosition : game?.fsPosition;
  (Array.isArray(positions) ? positions : []).forEach((pos, i) => {
    const role = normalizeRole(pos);
    if (!MATCHUP_ROLES.includes(role) || out[role]) return;
    out[role] = heroNameOf((Array.isArray(picks) ? picks : [])[i]);
  });

  return out;
}

function bump(acc, key, isWin, role = "") {
  if (!key) return;
  const slot = acc.get(key) || { games: 0, wins: 0, roles: new Set() };
  slot.games += 1;
  if (isWin) slot.wins += 1;
  if (role) slot.roles.add(role);
  acc.set(key, slot);
}

function toRows(acc) {
  return [...acc.entries()]
    .map(([name, v]) => ({
      name,
      games: v.games,
      wins: v.wins,
      losses: v.games - v.wins,
      winRate: v.games ? Number(((v.wins / v.games) * 100).toFixed(1)) : 0,
      // ตำแหน่งที่เจอ ใช้เลือกรูปของฮีโร่ที่มีหลายร่าง (Flowborn) — ว่างถ้าเจอหลายเลน
      role: v.roles?.size === 1 ? [...v.roles][0] : "",
    }))
    .sort((a, b) => b.games - a.games || b.winRate - a.winRate || a.name.localeCompare(b.name));
}

/**
 * @param games  รายการเกมจาก parseGamesFromExcel (กรองช่วงเวลา/ทีมมาก่อนได้)
 * @param members [{ hero, role }] — ฮีโร่ที่ต้องอยู่ในทีมฝั่งที่สนใจครบทุกตัว
 *                (role ใส่ไว้เพื่อบังคับว่าเล่นตำแหน่งนั้น เช่น Violet ที่เล่น ADL เท่านั้น)
 * @param side   'us' = สนใจคอมของ FS, 'them' = สนใจคอมของฝ่ายตรงข้าม (วินเรทกลับด้าน)
 * @returns { totalGames, wins, losses, winRate, lane, enemies, teams }
 *          lane    = ฮีโร่ที่เจอในตำแหน่งเดียวกับสมาชิกคอม
 *          enemies = ฮีโร่ฝ่ายตรงข้ามทั้ง 5 ตำแหน่ง
 *          teams   = แยกตามทีมที่เจอ
 */
export function buildHeroMatchups(games, { members = [], side = "us" } = {}) {
  const wanted = members
    .map((m) => (typeof m === "string" ? { hero: m, role: "" } : m))
    .map((m) => ({ hero: canonicalHeroName(m?.hero || ""), role: normalizeRole(m?.role || "") }))
    .filter((m) => m.hero);

  const empty = {
    totalGames: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    lane: [],
    enemies: [],
    teams: [],
  };
  if (!wanted.length) return empty;

  const mineSide = side === "them" ? "op" : "fs";
  const theirSide = side === "them" ? "fs" : "op";
  const laneAcc = new Map();
  const enemyAcc = new Map();
  const teamAcc = new Map();
  let totalGames = 0;
  let wins = 0;

  for (const game of Array.isArray(games) ? games : []) {
    const mine = rolePicks(game, mineSide);
    const theirs = rolePicks(game, theirSide);

    // ตำแหน่งของสมาชิกแต่ละตัวในเกมนี้ (ถ้าระบุ role มา ต้องตรงตำแหน่งนั้น)
    const rolesOfMembers = wanted.map((m) =>
      m.role
        ? (mine[m.role] === m.hero ? m.role : "")
        : (MATCHUP_ROLES.find((r) => mine[r] === m.hero) || "")
    );
    if (rolesOfMembers.some((r) => !r)) continue;

    const isWin = side === "them" ? !isFsWin(game) : isFsWin(game);
    totalGames += 1;
    if (isWin) wins += 1;

    // นับฮีโร่หนึ่งตัวได้ครั้งเดียวต่อเกม แต่จำตำแหน่งที่เจอไว้ด้วย
    const countOnce = (acc, roles) => {
      const seen = new Set();
      roles.forEach((r) => {
        const hero = theirs[r];
        if (!hero || seen.has(hero)) return;
        seen.add(hero);
        bump(acc, hero, isWin, r);
      });
    };
    countOnce(laneAcc, rolesOfMembers);
    countOnce(enemyAcc, MATCHUP_ROLES);
    bump(teamAcc, side === "them" ? "FS" : String(game?.competitor || "").trim(), isWin);
  }

  return {
    totalGames,
    wins,
    losses: totalGames - wins,
    winRate: totalGames ? Number(((wins / totalGames) * 100).toFixed(1)) : 0,
    lane: toRows(laneAcc),
    enemies: toRows(enemyAcc),
    teams: toRows(teamAcc),
  };
}

/**
 * "เจอตัวนี้แล้วเราตอบด้วยอะไร" — กลับด้านจาก buildHeroMatchups
 *
 * ใช้ตอนกดแถวศัตรูในแผง: ล็อกว่าฝ่ายตรงข้ามเล่น enemy ในตำแหน่ง role แล้วดูว่า
 * ฝั่งเราหยิบใครลงตำแหน่งเดียวกัน ผลเป็นยังไง — คำถามที่ใช้ตอนดราฟจริง
 *
 * @param games   log เกม (กรองช่วง/ทีมมาแล้ว)
 * @param enemy   ฮีโร่ของอีกฝ่าย
 * @param role    ตำแหน่งที่เจอกัน ("" = ตำแหน่งไหนก็ได้ที่ enemy ลง)
 * @param side    'us' = เรามองจากฝั่ง FS (enemy อยู่ฝั่ง op), 'them' = กลับด้าน
 * @returns { totalGames, wins, losses, winRate, answers }
 *          answers = ฮีโร่ฝั่งเราในตำแหน่งเดียวกัน เรียงตามจำนวนเกม
 */
export function buildCounterPicks(games, { enemy, role = "", side = "us" } = {}) {
  const wantedEnemy = canonicalHeroName(enemy || "");
  const wantedRole = normalizeRole(role || "");
  const empty = { totalGames: 0, wins: 0, losses: 0, winRate: 0, answers: [] };
  if (!wantedEnemy) return empty;

  const mineSide = side === "them" ? "op" : "fs";
  const theirSide = side === "them" ? "fs" : "op";
  const acc = new Map();
  let totalGames = 0;
  let wins = 0;

  for (const game of Array.isArray(games) ? games : []) {
    const mine = rolePicks(game, mineSide);
    const theirs = rolePicks(game, theirSide);
    const roles = wantedRole
      ? (theirs[wantedRole] === wantedEnemy ? [wantedRole] : [])
      : MATCHUP_ROLES.filter((r) => theirs[r] === wantedEnemy);
    if (!roles.length) continue;

    const isWin = side === "them" ? !isFsWin(game) : isFsWin(game);
    totalGames += 1;
    if (isWin) wins += 1;

    const seen = new Set();
    roles.forEach((r) => {
      const hero = mine[r];
      if (!hero || seen.has(hero)) return;
      seen.add(hero);
      bump(acc, hero, isWin, r);
    });
  }

  return {
    totalGames,
    wins,
    losses: totalGames - wins,
    winRate: totalGames ? Number(((wins / totalGames) * 100).toFixed(1)) : 0,
    answers: toRows(acc),
  };
}
